import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Activity,
  AlertCircle,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  FileText,
  Gauge,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  RefreshCcw,
  Save,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  UserCheck,
  UserPlus,
  UserRound,
  UserX,
  Users,
  X,
} from "lucide-react";

import API from "../../services/api";

import "../../styles/WorkerManager.css";


/* =========================================================
   CONFIG
========================================================= */

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:8000/api";

const API_ROOT = API_BASE_URL.replace(/\/+$/, "");

const WORKERS_ENDPOINT = `${API_ROOT}/workers`;


/* =========================================================
   INITIAL FORM
========================================================= */

const INITIAL_FORM = {
  document_number: "",
  position: "",
  department: "",
  phone: "",
  is_active: true,
};


/* =========================================================
   HELPERS
========================================================= */

const getApiError = (error, fallback = "Ocurrió un error inesperado.") => {
  const data = error?.response?.data;

  if (!data) {
    return fallback;
  }

  if (typeof data === "string") {
    return data;
  }

  if (data.detail) {
    return data.detail;
  }

  if (data.message) {
    return data.message;
  }

  const keys = Object.keys(data);

  if (keys.length) {
    const firstKey = keys[0];
    const value = data[firstKey];

    if (Array.isArray(value)) {
      return `${firstKey}: ${value[0]}`;
    }

    if (typeof value === "object" && value !== null) {
      return `${firstKey}: ${JSON.stringify(value)}`;
    }

    return `${firstKey}: ${String(value)}`;
  }

  return fallback;
};


const getAvatarUrl = (worker) => {
  const avatar = worker?.user?.avatar;

  if (!avatar) {
    return null;
  }

  if (/^(https?:|blob:|data:)/i.test(avatar)) {
    return avatar;
  }

  const base =
    API?.defaults?.baseURL
      ? API.defaults.baseURL.replace(/\/api\/?$/, "")
      : API_ROOT.replace(/\/api\/?$/, "");

  return `${base}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};


const getWorkerName = (worker) => {
  const user = worker?.user || {};

  return (
    user.full_name ||
    user.name ||
    user.username ||
    worker?.name ||
    "Trabajador"
  );
};


const getWorkerEmail = (worker) => {
  return worker?.user?.email || worker?.email || "Sin correo";
};


const getWorkerInitial = (worker) => {
  const name = getWorkerName(worker);

  return name?.trim()?.charAt(0)?.toUpperCase() || "U";
};


/* =========================================================
   COMPONENT
========================================================= */

export default function WorkerManager({
  refreshKey = 0,
}) {
  /* -------------------------------------------------------
     STATE
  ------------------------------------------------------- */

  const [workers, setWorkers] = useState([]);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);

  const [form, setForm] = useState({
    ...INITIAL_FORM,
  });

  /* Imagen */
  const [avatarFile, setAvatarFile] = useState(null);

  const [avatarPreview, setAvatarPreview] = useState(null);

  const [removeAvatar, setRemoveAvatar] = useState(false);


  /* -------------------------------------------------------
     AUTH HEADERS
  ------------------------------------------------------- */

  const getHeaders = useCallback(() => {
    try {
      const authRaw = localStorage.getItem("auth");

      if (!authRaw) {
        return {};
      }

      const auth = JSON.parse(authRaw);

      const token =
        auth?.access ||
        auth?.token ||
        auth?.access_token;

      if (!token) {
        return {};
      }

      return {
        Authorization: `Bearer ${token}`,
      };
    } catch (err) {
      console.error("Error leyendo auth:", err);

      return {};
    }
  }, []);


  /* -------------------------------------------------------
     LOAD WORKERS
  ------------------------------------------------------- */

  const loadWorkers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await API.get(
        `${WORKERS_ENDPOINT}/`,
        {
          headers: getHeaders(),
        }
      );

      const raw = response?.data;

      let list = [];

      if (Array.isArray(raw)) {
        list = raw;
      } else if (Array.isArray(raw?.results)) {
        list = raw.results;
      } else if (Array.isArray(raw?.data)) {
        list = raw.data;
      } else if (Array.isArray(raw?.workers)) {
        list = raw.workers;
      }

      setWorkers(list);
    } catch (err) {
      console.error("Error cargando trabajadores:", err);

      setError(
        getApiError(
          err,
          "No fue posible cargar los trabajadores."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);


  /* -------------------------------------------------------
     INITIAL LOAD
  ------------------------------------------------------- */

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers, refreshKey]);


  /* -------------------------------------------------------
     CLEAN AVATAR OBJECT URL
  ------------------------------------------------------- */

  useEffect(() => {
    return () => {
      if (
        avatarPreview &&
        avatarPreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);


  /* -------------------------------------------------------
     AUTO CLEAR ALERTS
  ------------------------------------------------------- */

  useEffect(() => {
    if (!success && !error) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setSuccess("");
      setError("");
    }, 6000);

    return () => clearTimeout(timer);
  }, [success, error]);


  /* -------------------------------------------------------
     BODY LOCK + ESC MODAL
  ------------------------------------------------------- */

  useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        setModalOpen(false);
        setEditing(null);
        setForm({
          ...INITIAL_FORM,
        });
        setAvatarFile(null);
        setAvatarPreview(null);
        setRemoveAvatar(false);
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [modalOpen, saving]);


  /* -------------------------------------------------------
     FORM
  ------------------------------------------------------- */

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };


  /* -------------------------------------------------------
     RESET
  ------------------------------------------------------- */

  const resetForm = () => {
    setForm({
      ...INITIAL_FORM,
    });

    setEditing(null);

    setAvatarFile(null);

    setAvatarPreview(null);

    setRemoveAvatar(false);
  };


  /* -------------------------------------------------------
     OPEN CREATE
  ------------------------------------------------------- */

  const openCreateModal = () => {
    setError("");

    setSuccess("");

    resetForm();

    setModalOpen(true);
  };


  /* -------------------------------------------------------
     OPEN EDIT
  ------------------------------------------------------- */

  const startEdit = (worker) => {
    setError("");

    setSuccess("");

    setEditing(worker);

    setForm({
      document_number:
        worker?.document_number || "",

      position:
        worker?.position || "",

      department:
        worker?.department || "",

      phone:
        worker?.phone || "",

      is_active:
        worker?.is_active !== false,
    });

    setAvatarFile(null);

    setRemoveAvatar(false);

    setAvatarPreview(
      getAvatarUrl(worker)
    );

    setModalOpen(true);
  };


  /* -------------------------------------------------------
     CLOSE MODAL
  ------------------------------------------------------- */

  const closeModal = () => {
    if (saving) {
      return;
    }

    setModalOpen(false);

    resetForm();
  };


  /* -------------------------------------------------------
     AVATAR CHANGE
  ------------------------------------------------------- */

  const handleAvatarChange = (event) => {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    setError("");

    if (!file.type.startsWith("image/")) {
      setError(
        "Selecciona una imagen válida."
      );

      event.target.value = "";

      return;
    }

    if (
      file.size >
      5 * 1024 * 1024
    ) {
      setError(
        "La imagen no puede superar los 5 MB."
      );

      event.target.value = "";

      return;
    }

    if (
      avatarPreview &&
      avatarPreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(
        avatarPreview
      );
    }

    const preview =
      URL.createObjectURL(file);

    setAvatarFile(file);

    setAvatarPreview(preview);

    setRemoveAvatar(false);
  };


  /* -------------------------------------------------------
     REMOVE AVATAR
  ------------------------------------------------------- */

  const handleRemoveAvatar = () => {
    if (
      avatarPreview &&
      avatarPreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(
        avatarPreview
      );
    }

    setAvatarFile(null);

    setAvatarPreview(null);

    setRemoveAvatar(true);
  };


  /* -------------------------------------------------------
     SAVE WORKER
  ------------------------------------------------------- */

  const saveWorker = async (event) => {
    event?.preventDefault();

    setError("");

    setSuccess("");

    const documentNumber =
      form.document_number.trim();

    const position =
      form.position.trim();

    const department =
      form.department.trim();

    const phone =
      form.phone.trim();

    if (!documentNumber) {
      setError(
        "El número de documento es obligatorio."
      );

      return;
    }

    if (!position) {
      setError(
        "El cargo es obligatorio."
      );

      return;
    }

    try {
      setSaving(true);

      /*
       * IMPORTANTE:
       * Usamos FormData porque ahora
       * también podemos enviar avatar.
       */

      const data = new FormData();

      data.append(
        "document_number",
        documentNumber
      );

      data.append(
        "position",
        position
      );

      data.append(
        "department",
        department
      );

      data.append(
        "phone",
        phone
      );

      data.append(
        "is_active",
        form.is_active
          ? "true"
          : "false"
      );

      if (avatarFile) {
        data.append(
          "avatar",
          avatarFile
        );
      }

      /*
       * Si tu backend implementa
       * remove_avatar, esto permite
       * eliminar la foto actual.
       */
      if (
        removeAvatar &&
        !avatarFile
      ) {
        data.append(
          "remove_avatar",
          "true"
        );
      }

      if (editing?.id) {
        await API.put(
          `${WORKERS_ENDPOINT}/${editing.id}/`,
          data,
          {
            headers: {
              ...getHeaders(),

              /*
               * NO establecer Content-Type
               * manualmente. Axios genera
               * automáticamente el boundary.
               */
            },
          }
        );

        setSuccess(
          "Trabajador actualizado correctamente."
        );
      } else {
        await API.post(
          `${WORKERS_ENDPOINT}/`,
          data,
          {
            headers: {
              ...getHeaders(),
            },
          }
        );

        setSuccess(
          "Trabajador creado correctamente."
        );
      }

      setModalOpen(false);

      resetForm();

      await loadWorkers();
    } catch (err) {
      console.error(
        "Error guardando trabajador:",
        err
      );

      setError(
        getApiError(
          err,
          "No fue posible guardar el trabajador."
        )
      );
    } finally {
      setSaving(false);
    }
  };


  /* -------------------------------------------------------
     DELETE
  ------------------------------------------------------- */

  const deleteWorker = async (worker) => {
    const name =
      getWorkerName(worker);

    const confirmed =
      window.confirm(
        `¿Seguro que deseas eliminar a ${name}?`
      );

    if (!confirmed) {
      return;
    }

    try {
      setError("");

      setSuccess("");

      await API.delete(
        `${WORKERS_ENDPOINT}/${worker.id}/`,
        {
          headers: getHeaders(),
        }
      );

      setWorkers((previous) =>
        previous.filter(
          (item) =>
            item.id !== worker.id
        )
      );

      setSuccess(
        "Trabajador eliminado correctamente."
      );
    } catch (err) {
      console.error(
        "Error eliminando trabajador:",
        err
      );

      setError(
        getApiError(
          err,
          "No fue posible eliminar el trabajador."
        )
      );
    }
  };


  /* -------------------------------------------------------
     FILTER
  ------------------------------------------------------- */

  const filtered = useMemo(() => {
    const query =
      search.trim().toLowerCase();

    if (!query) {
      return workers;
    }

    return workers.filter((worker) => {
      const user =
        worker?.user || {};

      const values = [
        user.username,
        user.email,
        user.first_name,
        user.last_name,
        worker?.document_number,
        worker?.position,
        worker?.department,
        worker?.phone,
      ];

      return values.some(
        (value) =>
          value &&
          String(value)
            .toLowerCase()
            .includes(query)
      );
    });
  }, [workers, search]);


  /* -------------------------------------------------------
     STATS
  ------------------------------------------------------- */

  const stats = useMemo(() => {
    const total =
      workers.length;

    const active =
      workers.filter(
        (worker) =>
          worker?.is_active !== false
      ).length;

    const inactive =
      total - active;

    const departments =
      new Set(
        workers
          .map(
            (worker) =>
              worker?.department
          )
          .filter(Boolean)
      ).size;

    const activeRate =
      total > 0
        ? Math.round(
            (active / total) * 100
          )
        : 0;

    return {
      total,
      active,
      inactive,
      departments,
      activeRate,
    };
  }, [workers]);


  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <section className="worker-manager">

      {/* =====================================================
          HERO
      ===================================================== */}

      <header className="wm-hero">

        <div className="wm-hero-copy">

          <div className="wm-eyebrow">
            <span className="wm-eyebrow-dot" />
            DEBUTS CROP · PEOPLE SYSTEM
          </div>

          <div className="wm-title-row">

            <div className="wm-title-icon">
              <Users size={25} />
            </div>

            <div>
              <h1>
                Equipo
                <span>/</span>
                Trabajadores
              </h1>

              <p>
                Administra perfiles,
                cargos, departamentos
                y acceso operativo.
              </p>
            </div>

          </div>

        </div>


        <div className="wm-hero-actions">

          <button
            type="button"
            className="wm-btn wm-btn-secondary"
            onClick={loadWorkers}
            disabled={loading}
          >
            {loading ? (
              <Loader2
                size={17}
                className="wm-spin"
              />
            ) : (
              <RefreshCcw size={17} />
            )}

            <span>
              Actualizar
            </span>
          </button>


          <button
            type="button"
            className="wm-btn wm-btn-primary"
            onClick={openCreateModal}
          >
            <UserPlus size={17} />

            <span>
              Nuevo trabajador
            </span>

            <ArrowUpRight size={16} />
          </button>

        </div>

      </header>


      {/* =====================================================
          STATS
      ===================================================== */}

      <div className="wm-stats">

        <div className="wm-stat-card wm-stat-pink">

          <div className="wm-stat-icon">
            <Users size={19} />
          </div>

          <div className="wm-stat-content">
            <span>
              Total equipo
            </span>

            <strong>
              {stats.total}
            </strong>
          </div>

          <Sparkles
            size={17}
            className="wm-stat-decoration"
          />

        </div>


        <div className="wm-stat-card wm-stat-lime">

          <div className="wm-stat-icon">
            <UserCheck size={19} />
          </div>

          <div className="wm-stat-content">
            <span>
              Activos
            </span>

            <strong>
              {stats.active}
            </strong>
          </div>

          <CheckCircle2
            size={17}
            className="wm-stat-decoration"
          />

        </div>


        <div className="wm-stat-card wm-stat-orange">

          <div className="wm-stat-icon">
            <UserX size={19} />
          </div>

          <div className="wm-stat-content">
            <span>
              Inactivos
            </span>

            <strong>
              {stats.inactive}
            </strong>
          </div>

        </div>


        <div className="wm-stat-card wm-stat-cyan">

          <div className="wm-stat-icon">
            <Building2 size={19} />
          </div>

          <div className="wm-stat-content">
            <span>
              Departamentos
            </span>

            <strong>
              {stats.departments}
            </strong>
          </div>

        </div>


        <div className="wm-stat-card wm-stat-purple">

          <div className="wm-stat-icon">
            <Gauge size={19} />
          </div>

          <div className="wm-stat-content">
            <span>
              Operación activa
            </span>

            <strong>
              {stats.activeRate}%
            </strong>
          </div>

        </div>

      </div>


      {/* =====================================================
          ALERTS
      ===================================================== */}

      {error && (
        <div className="wm-alert wm-alert-error">

          <AlertCircle size={18} />

          <div>
            <strong>
              Atención
            </strong>

            <span>
              {error}
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              setError("")
            }
            aria-label="Cerrar mensaje"
          >
            <X size={17} />
          </button>

        </div>
      )}


      {success && (
        <div className="wm-alert wm-alert-success">

          <CheckCircle2 size={18} />

          <div>
            <strong>
              Operación completada
            </strong>

            <span>
              {success}
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              setSuccess("")
            }
            aria-label="Cerrar mensaje"
          >
            <X size={17} />
          </button>

        </div>
      )}


      {/* =====================================================
          SEARCH
      ===================================================== */}

      <div className="wm-toolbar">

        <div className="wm-search">

          <Search size={18} />

          <input
            type="search"
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Buscar por nombre, correo, documento, cargo..."
            aria-label="Buscar trabajadores"
          />

          {search && (
            <button
              type="button"
              className="wm-search-clear"
              onClick={() =>
                setSearch("")
              }
              aria-label="Limpiar búsqueda"
            >
              <X size={15} />
            </button>
          )}

        </div>


        <div className="wm-results-count">

          <Activity size={16} />

          <span>
            Mostrando
          </span>

          <strong>
            {filtered.length}
          </strong>

          <span>
            de
          </span>

          <strong>
            {workers.length}
          </strong>

        </div>

      </div>


      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading ? (
        <div className="wm-state">

          <div className="wm-loader">

            <Loader2
              size={34}
              className="wm-spin"
            />

          </div>

          <strong>
            Cargando equipo...
          </strong>

          <span>
            Estamos sincronizando
            los trabajadores.
          </span>

        </div>
      ) : filtered.length === 0 ? (

        /* ===================================================
           EMPTY
        =================================================== */

        <div className="wm-state wm-empty">

          <div className="wm-empty-icon">
            {search ? (
              <Search size={31} />
            ) : (
              <Users size={31} />
            )}
          </div>

          <strong>
            {search
              ? "Sin resultados"
              : "Todavía no hay trabajadores"}
          </strong>

          <span>
            {search
              ? "Prueba con otro término de búsqueda."
              : "Crea el primer perfil del equipo para comenzar."}
          </span>

          {!search && (
            <button
              type="button"
              className="wm-btn wm-btn-primary"
              onClick={openCreateModal}
            >
              <Plus size={17} />
              Crear trabajador
            </button>
          )}

        </div>

      ) : (

        /* ===================================================
           GRID
        =================================================== */

        <div className="wm-grid">

          {filtered.map(
            (worker, index) => {
              const avatar =
                getAvatarUrl(worker);

              const name =
                getWorkerName(worker);

              const email =
                getWorkerEmail(worker);

              const initial =
                getWorkerInitial(worker);

              const active =
                worker?.is_active !== false;

              return (
                <article
                  className="wm-card"
                  key={
                    worker.id ||
                    `worker-${index}`
                  }
                >

                  <div className="wm-card-glow" />

                  <div className="wm-card-index">
                    #{String(index + 1).padStart(2, "0")}
                  </div>


                  {/* PROFILE */}

                  <div className="wm-profile">

                    <div className="wm-avatar">

                      {avatar ? (
                        <img
                          src={avatar}
                          alt={`Perfil de ${name}`}
                        />
                      ) : (
                        <span>
                          {initial}
                        </span>
                      )}

                      <span
                        className={
                          active
                            ? "wm-avatar-status active"
                            : "wm-avatar-status"
                        }
                      />

                    </div>


                    <div className="wm-profile-main">

                      <div className="wm-status-row">

                        <span
                          className={
                            active
                              ? "wm-status active"
                              : "wm-status"
                          }
                        >
                          <span />

                          {active
                            ? "Activo"
                            : "Inactivo"}
                        </span>

                      </div>

                      <h3>
                        {name}
                      </h3>

                      <p>
                        <Mail size={13} />

                        {email}
                      </p>

                    </div>

                  </div>


                  {/* POSITION */}

                  <div className="wm-role">

                    <span>
                      CARGO
                    </span>

                    <strong>
                      {worker?.position ||
                        "Sin cargo definido"}
                    </strong>

                  </div>


                  {/* INFO */}

                  <div className="wm-info-grid">

                    <div className="wm-info-item">

                      <div>
                        <FileText size={15} />
                      </div>

                      <span>
                        Documento
                      </span>

                      <strong>
                        {worker?.document_number ||
                          "—"}
                      </strong>

                    </div>


                    <div className="wm-info-item">

                      <div>
                        <Building2 size={15} />
                      </div>

                      <span>
                        Departamento
                      </span>

                      <strong>
                        {worker?.department ||
                          "Sin asignar"}
                      </strong>

                    </div>


                    <div className="wm-info-item">

                      <div>
                        <Phone size={15} />
                      </div>

                      <span>
                        Teléfono
                      </span>

                      <strong>
                        {worker?.phone ||
                          "Sin teléfono"}
                      </strong>

                    </div>


                    <div className="wm-info-item">

                      <div>
                        <ShieldCheck size={15} />
                      </div>

                      <span>
                        Acceso
                      </span>

                      <strong>
                        {active
                          ? "Habilitado"
                          : "Suspendido"}
                      </strong>

                    </div>

                  </div>


                  {/* FOOTER */}

                  <footer className="wm-card-footer">

                    <span>
                      ID ·{" "}
                      {worker?.id || "—"}
                    </span>


                    <div className="wm-card-actions">

                      <button
                        type="button"
                        className="wm-icon-btn wm-edit-btn"
                        onClick={() =>
                          startEdit(worker)
                        }
                        title="Editar trabajador"
                        aria-label={`Editar ${name}`}
                      >
                        <Pencil size={16} />
                      </button>


                      <button
                        type="button"
                        className="wm-icon-btn wm-delete-btn"
                        onClick={() =>
                          deleteWorker(worker)
                        }
                        title="Eliminar trabajador"
                        aria-label={`Eliminar ${name}`}
                      >
                        <Trash2 size={16} />
                      </button>

                    </div>

                  </footer>

                </article>
              );
            }
          )}

        </div>
      )}


      {/* =====================================================
          MODAL
      ===================================================== */}

      {modalOpen && (
        <div
          className="wm-modal-overlay"
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !saving
            ) {
              closeModal();
            }
          }}
        >

          <div
            className="wm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="worker-modal-title"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >

            {/* MODAL HEADER */}

            <header className="wm-modal-header">

              <div className="wm-modal-heading">

                <div className="wm-modal-icon">
                  {editing ? (
                    <Pencil size={20} />
                  ) : (
                    <UserPlus size={20} />
                  )}
                </div>

                <div>

                  <span>
                    DEBUTS CROP · PEOPLE
                  </span>

                  <h2 id="worker-modal-title">
                    {editing
                      ? "Editar trabajador"
                      : "Nuevo trabajador"}
                  </h2>

                </div>

              </div>


              <button
                type="button"
                className="wm-modal-close"
                onClick={closeModal}
                disabled={saving}
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>

            </header>


            {/* MODAL BODY */}

            <form
              className="wm-modal-form-wrapper"
              onSubmit={saveWorker}
            >

              <div className="wm-modal-body">

                {/* -------------------------------------------
                    PROFILE SIDE
                -------------------------------------------- */}

                <aside className="wm-modal-profile">

                  <div className="wm-modal-profile-label">
                    PERFIL
                  </div>


                  <div className="wm-large-avatar">

                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Vista previa del perfil"
                      />
                    ) : (
                      <UserRound
                        size={48}
                      />
                    )}

                    <label
                      htmlFor="worker-avatar"
                      className="wm-avatar-camera"
                      title="Subir foto"
                    >
                      <Upload size={17} />
                    </label>

                    <input
                      id="worker-avatar"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={
                        handleAvatarChange
                      }
                      hidden
                    />

                  </div>


                  <div className="wm-modal-profile-name">

                    <strong>
                      {editing
                        ? getWorkerName(
                            editing
                          )
                        : "Nuevo perfil"}
                    </strong>

                    <span>
                      {form.position ||
                        "Cargo por definir"}
                    </span>

                  </div>


                  <label
                    htmlFor="worker-avatar"
                    className="wm-upload-main"
                  >
                    <Upload size={16} />

                    {avatarPreview
                      ? "Cambiar imagen"
                      : "Subir imagen"}
                  </label>


                  {avatarPreview && (
                    <button
                      type="button"
                      className="wm-remove-avatar"
                      onClick={
                        handleRemoveAvatar
                      }
                    >
                      <Trash2 size={14} />
                      Quitar foto
                    </button>
                  )}


                  <div className="wm-avatar-help">

                    <Sparkles size={15} />

                    <span>
                      JPG, PNG o WEBP
                      <br />
                      Máximo 5 MB
                    </span>

                  </div>


                  <div className="wm-profile-note">

                    <ShieldCheck
                      size={17}
                    />

                    <p>
                      La información de
                      usuario y correo
                      pertenece al sistema
                      de autenticación.
                    </p>

                  </div>

                </aside>


                {/* -------------------------------------------
                    FORM SIDE
                -------------------------------------------- */}

                <div className="wm-fields">

                  <div className="wm-fields-heading">

                    <span>
                      DATOS OPERATIVOS
                    </span>

                    <p>
                      Completa la información
                      del integrante del equipo.
                    </p>

                  </div>


                  {/* DOCUMENT */}

                  <div className="wm-field">

                    <label htmlFor="document_number">
                      <FileText size={15} />

                      Número de documento

                      <b>*</b>
                    </label>

                    <input
                      id="document_number"
                      name="document_number"
                      type="text"
                      value={
                        form.document_number
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Ej. 1234567890"
                      autoComplete="off"
                      autoFocus
                    />

                  </div>


                  {/* POSITION */}

                  <div className="wm-field">

                    <label htmlFor="position">
                      <BriefcaseIcon />

                      Cargo

                      <b>*</b>
                    </label>

                    <input
                      id="position"
                      name="position"
                      type="text"
                      value={
                        form.position
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Ej. Administrador"
                      autoComplete="organization-title"
                    />

                  </div>


                  {/* DEPARTMENT */}

                  <div className="wm-field">

                    <label htmlFor="department">
                      <Building2 size={15} />

                      Departamento
                    </label>

                    <input
                      id="department"
                      name="department"
                      type="text"
                      value={
                        form.department
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Ej. Ventas"
                      autoComplete="organization"
                    />

                  </div>


                  {/* PHONE */}

                  <div className="wm-field">

                    <label htmlFor="phone">
                      <Phone size={15} />

                      Teléfono
                    </label>

                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={
                        form.phone
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Ej. 300 000 0000"
                      autoComplete="tel"
                    />

                  </div>


                  {/* STATUS */}

                  <div className="wm-status-editor">

                    <div className="wm-status-editor-copy">

                      <div className="wm-status-editor-icon">
                        <Activity
                          size={17}
                        />
                      </div>

                      <div>

                        <strong>
                          Estado del trabajador
                        </strong>

                        <span>
                          Define si puede operar
                          dentro del sistema.
                        </span>

                      </div>

                    </div>


                    <label className="wm-switch">

                      <input
                        type="checkbox"
                        name="is_active"
                        checked={
                          form.is_active
                        }
                        onChange={
                          handleChange
                        }
                      />

                      <span className="wm-switch-track">
                        <span />
                      </span>

                      <b>
                        {form.is_active
                          ? "ACTIVO"
                          : "INACTIVO"}
                      </b>

                    </label>

                  </div>


                  {/* INFO */}

                  <div className="wm-form-info">

                    <AlertCircle size={16} />

                    <span>
                      Los campos marcados con
                      <strong>*</strong> son
                      obligatorios.
                    </span>

                  </div>

                </div>

              </div>


              {/* MODAL FOOTER */}

              <footer className="wm-modal-footer">

                <button
                  type="button"
                  className="wm-btn wm-btn-cancel"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancelar
                </button>


                <button
                  type="submit"
                  className="wm-btn wm-btn-primary wm-save-btn"
                  disabled={saving}
                >

                  {saving ? (
                    <>
                      <Loader2
                        size={17}
                        className="wm-spin"
                      />

                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save size={17} />

                      {editing
                        ? "Guardar cambios"
                        : "Crear trabajador"}
                    </>
                  )}

                </button>

              </footer>

            </form>

          </div>

        </div>
      )}

    </section>
  );
}


/* =========================================================
   SMALL ICON HELPER
   Evita importar otro icono solo para el campo Cargo.
========================================================= */

function BriefcaseIcon() {
  return (
    <span className="wm-inline-icon">
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect
          width="20"
          height="14"
          x="2"
          y="7"
          rx="2"
          ry="2"
        />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        <path d="M2 12h20" />
      </svg>
    </span>
  );
}